import type { Body, LevelDoc, PlacedObject, TileLayer } from '../model/format.ts';
import { compose, mul, type Xform } from '../model/xform.ts';
import { num } from '../model/unityProps.ts';
import { terrainTiles, effectiveTiles } from '../model/bake.ts';
import type { TerrainContainer } from '../terrain/index.ts';
import { sortKey, sortingLayerNameFromId, spriteFromRef } from '../data/gameData.ts';
import {
  colorOf, instanceBounds, instanceDrawables, instantiateObject, type Drawable, type InstGo, type PrefabInstance, type SpriteDrawable,
} from '../data/prefabInstance.ts';
import { TileLayerView, drawableItem, localView, type SceneItem } from '../render/renderer.ts';
import { crumbleGenerator, isPrefabTile } from '../data/crumble.ts';

// ---------- caches keyed by immutable document objects ----------

interface ObjInfo {
  inst: PrefabInstance | null;
  drawables: Drawable[];
  bounds: [number, number, number, number];
}

const objCache = new WeakMap<PlacedObject, ObjInfo>();
const layerViews = new Map<string, TileLayerView>();
const prefabTileViews = new Map<string, TileLayerView>();
const effCache = new WeakMap<number[], { gen: number[] | undefined; out: number[] }>();

/** GameObjects inside an instance that belong to an owned body (door halves) and are drawn by the body. */
function ownedHandlerGos(inst: PrefabInstance, handlerIds: string[]): Set<InstGo> {
  const out = new Set<InstGo>();
  for (const hid of handlerIds) {
    const n = inst.nodes.get(hid);
    if (!n) continue;
    const walk = (g: InstGo) => {
      if (g.name.startsWith('MoveTo')) return;
      out.add(g);
      g.children.forEach(walk);
    };
    n.go.children.forEach(walk);
  }
  return out;
}

let ownerMap = new Map<string, string[]>();
let ownerDoc: LevelDoc | null = null;
function ownersOf(doc: LevelDoc) {
  if (ownerDoc !== doc) {
    ownerMap = new Map();
    for (const b of doc.bodies) if (b.owner) ownerMap.set(b.owner.object, [...(ownerMap.get(b.owner.object) ?? []), b.owner.handlerSourceId]);
    ownerDoc = doc;
  }
  return ownerMap;
}

export function objectInfo(doc: LevelDoc, o: PlacedObject): ObjInfo {
  let info = objCache.get(o);
  if (info) return info;
  let drawables: Drawable[] = [];
  let inst: PrefabInstance | null = null;
  if (o.prefab) {
    inst = instantiateObject(o);
    if (inst) {
      const handlers = ownersOf(doc).get(o.id);
      drawables = instanceDrawables(inst);
      if (handlers) {
        const skip = ownedHandlerGos(inst, handlers);
        drawables = drawables.filter((d) => !skip.has(d.go));
      }
    }
  } else {
    drawables = customDrawables(o);
  }
  // a crumble platform shows its tiled sprite; its tiles are generated on play / Unity import
  const crumble = inst ? crumbleGenerator(o) : null;
  if (crumble) drawables = drawables.filter((d) => !(d.go.parent === inst!.root && isPrefabTile(crumble, d.go.name)));
  if (o.active === false) drawables = [];
  let bounds: [number, number, number, number] = [o.x - 0.5, o.y - 0.5, o.x + 0.5, o.y + 0.5];
  if (crumble) bounds = drawablesBounds(drawables) ?? bounds;
  else if (inst) bounds = instanceBounds(inst) ?? bounds;
  else if (drawables.length) bounds = drawablesBounds(drawables) ?? bounds;
  else bounds = customColliderBounds(o) ?? bounds;
  info = { inst, drawables, bounds };
  objCache.set(o, info);
  return info;
}

function customDrawables(o: PlacedObject): Drawable[] {
  const out: Drawable[] = [];
  const world = compose(o.x, o.y, o.rotation, o.scale[0], o.scale[1]);
  const go = { id: o.id, name: o.name } as InstGo;
  const maskComp = (o.components ?? []).find((c) => c.type === 'SpriteMask' && String(c.data.m_Enabled ?? '1') !== '0');
  const maskSprite = maskComp ? spriteFromRef(maskComp.data.m_Sprite) : null;
  for (const c of o.components ?? []) {
    if (c.type !== 'SpriteRenderer' || String(c.data.m_Enabled ?? '1') === '0') continue;
    const sprite = spriteFromRef(c.data.m_Sprite);
    if (!sprite) continue;
    const maskInteraction = num(c.data.m_MaskInteraction);
    out.push({
      kind: 'sprite', sprite, world, color: colorOf(c.data.m_Color), flipX: String(c.data.m_FlipX) === '1', flipY: String(c.data.m_FlipY) === '1',
      sortingLayer: sortingLayerNameFromId(c.data.m_SortingLayerID), order: num(c.data.m_SortingOrder), drawMode: num(c.data.m_DrawMode),
      size: [num(c.data.m_Size?.x, 1), num(c.data.m_Size?.y, 1)], tileMode: num(c.data.m_SpriteTileMode), tileThreshold: num(c.data.m_AdaptiveModeThreshold, 0.5),
      maskInteraction, mask: maskSprite && (maskInteraction === 1 || maskInteraction === 2) ? { sprite: maskSprite, world } : undefined, go,
    });
  }
  return out;
}

function customColliderBounds(o: PlacedObject): [number, number, number, number] | null {
  for (const c of o.components ?? []) {
    if (c.type === 'BoxCollider2D') {
      const sx = num(c.data.m_Size?.x, 1) * Math.abs(o.scale[0]), sy = num(c.data.m_Size?.y, 1) * Math.abs(o.scale[1]);
      const ox = o.x + num(c.data.m_Offset?.x) * o.scale[0], oy = o.y + num(c.data.m_Offset?.y) * o.scale[1];
      return [ox - sx / 2, oy - sy / 2, ox + sx / 2, oy + sy / 2];
    }
  }
  return null;
}

function drawablesBounds(ds: Drawable[]): [number, number, number, number] | null {
  let b: [number, number, number, number] | null = null;
  for (const d of ds) {
    if (d.kind !== 'sprite') continue;
    const s = d as SpriteDrawable;
    const w = s.drawMode ? s.size[0] : s.sprite.def.r[2] / s.sprite.texDef.ppu;
    const h = s.drawMode ? s.size[1] : s.sprite.def.r[3] / s.sprite.texDef.ppu;
    const x0 = d.world.tx - w / 2, y0 = d.world.ty - h / 2;
    b = b ? [Math.min(b[0], x0), Math.min(b[1], y0), Math.max(b[2], x0 + w), Math.max(b[3], y0 + h)] : [x0, y0, x0 + w, y0 + h];
  }
  return b;
}

/** Final tiles of a layer (terrain + manual), memoized by array identity. */
export function layerTiles(doc: LevelDoc, container: TerrainContainer, layer: TileLayer): number[] {
  const gen = terrainTiles(doc, container).get(layer.id);
  const hit = effCache.get(layer.tiles);
  if (hit && hit.gen === gen) return hit.out;
  const out = effectiveTiles(layer, gen);
  effCache.set(layer.tiles, { gen, out });
  return out;
}

// ---------- bodies ----------

export function bodyXform(b: Body, offset: [number, number] = [0, 0]): Xform {
  return compose(b.origin[0] + offset[0], b.origin[1] + offset[1], b.rotation ?? 0);
}

/** Movement settings while the body is a moving platform (kept, but inactive, under other types). */
export function movingOf(b: Body): Body['moving'] {
  return b.behavior === 'movingPlatform' ? b.moving : undefined;
}

/** Path the moving content follows: start (origin) then waypoints. */
export function bodyPath(b: Body): [number, number][] {
  const m = movingOf(b);
  if (!m) return [b.origin];
  return [b.origin, ...m.waypoints.map((w) => [w.x, w.y] as [number, number])];
}

export function pathPoint(pts: [number, number][], t: number): [number, number] {
  if (pts.length < 2) return pts[0];
  const segs = pts.length - 1;
  const f = Math.max(0, Math.min(1, t)) * segs;
  const i = Math.min(segs - 1, Math.floor(f));
  const u = f - i;
  return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * u, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * u];
}

export function bodyOffset(b: Body, scrub: number): [number, number] {
  if (!movingOf(b) || scrub <= 0) return [0, 0];
  const p = pathPoint(bodyPath(b), scrub);
  return [p[0] - b.origin[0], p[1] - b.origin[1]];
}

// ---------- scene ----------

export interface SceneOptions {
  view: [number, number, number, number];
  scrub: number;
  /** container being edited: others are dimmed */
  focus: string;
  hidden?: Set<string>;
}

export function buildScene(doc: LevelDoc, opt: SceneOptions): SceneItem[] {
  const items: SceneItem[] = [];
  const dim = (cid: string) => (opt.focus !== 'level' && opt.focus !== cid ? 0.35 : 1);
  const addLayers = (cid: string, container: { layers: TileLayer[]; terrains: any[] }, world: Xform, alpha: number) => {
    for (const l of container.layers) {
      if (l.visible === false) continue;
      const tiles = layerTiles(doc, container, l);
      let v = layerViews.get(l.id);
      if (!v) layerViews.set(l.id, (v = new TileLayerView()));
      v.set(tiles, doc.palette);
      const view = v;
      const m = l.offset ? mul(world, compose(l.offset[0], l.offset[1], 0)) : world;
      const color = (l.color ?? [1, 1, 1, 1]) as [number, number, number, number];
      items.push({
        key: sortKey(l.sortingLayer, l.sortingOrder),
        draw: (ctx) => {
          ctx.save();
          ctx.transform(m.a, m.b, m.c, m.d, m.tx, m.ty);
          if (alpha < 1) ctx.globalAlpha = alpha;
          view.draw(ctx, localView(m, opt.view), color);
          ctx.restore();
        },
      });
    }
    void cid;
  };
  addLayers('level', doc, compose(0, 0, 0), dim('level'));
  const addObj = (o: PlacedObject, alpha: number, offset: [number, number]) => {
    const info = objectInfo(doc, o);
    const b = info.bounds;
    if (b[2] + offset[0] < opt.view[0] - 2 || b[0] + offset[0] > opt.view[2] + 2 || b[3] + offset[1] < opt.view[1] - 2 || b[1] + offset[1] > opt.view[3] + 2) return;
    for (const d of info.drawables) items.push(drawableItem(shiftDrawable(d, offset), opt.view, prefabTileViews, alpha));
  };
  for (const o of doc.objects) addObj(o, dim('level'), [0, 0]);
  for (const b of doc.bodies) {
    if (b.hidden) continue;
    const off = bodyOffset(b, opt.scrub);
    const a = dim(b.id);
    addLayers(b.id, b, bodyXform(b, off), a);
    for (const o of b.objects) addObj(o, a, off);
  }
  return items;
}

/** Move a drawable and its sprite mask by the same world offset (body preview scrub). */
function shiftDrawable(d: Drawable, offset: [number, number]): Drawable {
  if (!offset[0] && !offset[1]) return d;
  const world = { ...d.world, tx: d.world.tx + offset[0], ty: d.world.ty + offset[1] };
  if (d.kind === 'sprite' && d.mask) return { ...d, world, mask: { sprite: d.mask.sprite, world: { ...d.mask.world, tx: d.mask.world.tx + offset[0], ty: d.mask.world.ty + offset[1] } } };
  return { ...d, world };
}

// ---------- picking ----------

export type Pick = { kind: 'object'; id: string } | { kind: 'body'; id: string } | null;

function pointInBody(doc: LevelDoc, b: Body, x: number, y: number, scrub: number): boolean {
  const off = bodyOffset(b, scrub);
  const m = bodyXform(b, off);
  const det = m.a * m.d - m.b * m.c || 1;
  const dx = x - m.tx, dy = y - m.ty;
  const lx = (m.d * dx - m.c * dy) / det, ly = (-m.b * dx + m.a * dy) / det;
  const cx = Math.floor(lx), cy = Math.floor(ly);
  for (const l of b.layers) {
    const t = layerTiles(doc, b, l);
    for (let i = 0; i < t.length; i += 4) if (t[i] === cx && t[i + 1] === cy && t[i + 2] >= 0) return true;
  }
  return false;
}

export function pickAt(doc: LevelDoc, x: number, y: number, scrub: number, preferBodies = false): Pick {
  let best: Pick = null;
  let bestArea = Infinity;
  const consider = (o: PlacedObject, off: [number, number]) => {
    const b = objectInfo(doc, o).bounds;
    if (x < b[0] + off[0] || x > b[2] + off[0] || y < b[1] + off[1] || y > b[3] + off[1]) return;
    const area = (b[2] - b[0]) * (b[3] - b[1]);
    if (area < bestArea) {
      bestArea = area;
      best = { kind: 'object', id: o.id };
    }
  };
  if (!preferBodies) {
    for (const b of doc.bodies) for (const o of b.objects) consider(o, bodyOffset(b, scrub));
    for (const o of doc.objects) consider(o, [0, 0]);
    if (best) return best;
  }
  for (let i = doc.bodies.length - 1; i >= 0; i--) {
    const b = doc.bodies[i];
    if (!b.hidden && pointInBody(doc, b, x, y, scrub)) return { kind: 'body', id: b.id };
  }
  if (preferBodies) {
    for (const b of doc.bodies) for (const o of b.objects) consider(o, bodyOffset(b, scrub));
    for (const o of doc.objects) consider(o, [0, 0]);
  }
  return best;
}

export function objectsInRect(doc: LevelDoc, r: [number, number, number, number]): string[] {
  const out: string[] = [];
  const all = [...doc.objects, ...doc.bodies.flatMap((b) => b.objects)];
  for (const o of all) {
    if (o.x >= r[0] && o.x <= r[2] && o.y >= r[1] && o.y <= r[3]) out.push(o.id);
  }
  return out;
}

export function bodyBounds(doc: LevelDoc, b: Body): [number, number, number, number] | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const l of b.layers) {
    const t = layerTiles(doc, b, l);
    for (let i = 0; i < t.length; i += 4) {
      if (t[i + 2] < 0) continue;
      x0 = Math.min(x0, t[i]); y0 = Math.min(y0, t[i + 1]); x1 = Math.max(x1, t[i] + 1); y1 = Math.max(y1, t[i + 1] + 1);
    }
  }
  return Number.isFinite(x0) ? [x0, y0, x1, y1] : null;
}
